import compareVersion from '@/utils/compareVersion';
import { appendParam } from '@/utils/url';
import Observe from '@/utils/observe';
import { platWindow } from '@/utils/env';
import { showLoadingToast } from '@/components/basic/LoadingToast';
import { callNativeWithPromiseByScheme } from '@/jsbridge/hippy/api/callNative';
import { transferUserInfo } from '@/utils/transfer-client-data';
import { getApp } from '../../utils/util';
import report from '../../utils/reportData';
import logger from '../../utils/logger';

const globalNotificationObserve = new Observe();

let superProps = null;

export default {
  install(Vue) {
    // h5屏蔽原生方法
    Vue.Native = Vue.Native || {
      callNativeWithPromise() {
        return Promise.resolve(null);
      },
      callNative() {
        return null;
      },
      callUIFunction() {
        return null;
      },
      Platform: 'browser',
      Dimensions: { // todo 确认和验证使用场景，服务端不支持获取这些数据
        screen: {
          height: platWindow.screen.width,
          width: platWindow.screen.height,
        },
        window: {
          height: platWindow.screen.availWidth,
          width: platWindow.screen.availHeight,
        },
      },
    };

    Vue.prototype.tools = {
      callNativeWithPromise(functionName, ...params) {
        if (functionName === 'getUserInfo') {
          return Vue.Native.callNativeWithPromise(
            'GameCommunityJsModule',
            functionName,
            ...params,
          )
            .then(res => transferUserInfo(res));
        }
        return Vue.Native.callNativeWithPromise(
          'GameCommunityJsModule',
          functionName,
          ...params,
        );
      },
      /**
       * 适用于需要终端多次回调的场景
       * @param {String} functionName 函数名
       * @param {Function} callback            params 第一参数 回调函数
       * @param {String}   instanceId          params 第二参数 实例ID
       * @param {String|Object|Boolean|Number} params 实际传参
       * @example
         this.tools.callNativeWithCallbackId(
           'showImagePreviewWithNavigationBar',
           callback,
           instanceId,
           JSON.stringify({
              index,
              images: this.dataList.map((item) => ({
                ...item,
                image: item.sourceImage || item.bigImage || item.image,
              })),
            });
       * */
      callNativeWithCallbackId(functionName, ...params) {
        const cb = params.shift();
        if (typeof cb !== 'function') {
          throw new TypeError('first argument must be function');
        }
        if (Vue.Native.Platform === 'ios') {
          return Vue.Native.callNativeWithCallbackId(
            'GameCommunityJsModule',
            functionName,
            false,
            ...params,
            cb,
          );
        }
        return Vue.Native.callNativeWithCallbackId(
          'GameCommunityJsModule',
          functionName,
          false, // 是否自动删除
          cb,
          ...params,
        );
      },

      callNative(functionName, ...params) {
        if (functionName === 'callNativeAction') {
          return Vue.Native.callNative(
            'GameCommunityJsModule',
            functionName,
            getApp().$options.rootViewId,
            ...params,
          );
        }
        return Vue.Native.callNative(
          'GameCommunityJsModule',
          functionName,
          ...params,
        );
      },

      callNativeWithPromiseByScheme,

      showToast(text) {
        // iOS 下，不接受非 string 类型
        if (typeof text !== 'string') {
          text = JSON.stringify(text);
        }
        return Vue.Native.callNative(
          'GameCommunityJsModule',
          'showToast',
          getApp().$options.rootViewId,
          text,
        );
      },

      showLoadingToast() {
        return showLoadingToast(getApp());
      },

      getNumberToString(numberValue, digits = 1) {
        if (
          typeof numberValue !== 'number'
        ) {
          return numberValue;
        }
        if (
          isNaN(numberValue)
          || numberValue <= 0
        ) {
          return '0';
        }
        const tmp = (numberValue / 10000).toFixed(digits);
        if (tmp > 1) {
          return `${tmp}w`;
        }
        // tmp = (numberValue / 1000).toFixed(1);
        // if (tmp > 1) {
        //   return tmp + 'k';
        // }
        return `${numberValue}`;
      },

      getTimeLabel(time) {
        if (typeof time !== 'number' || isNaN(time) || time <= 0) {
          return '刚刚';
        }

        const dateCurrent = new Date(); // 获取当前时间
        const dateReal = new Date(time); // 时间时间
        const dateDiff = dateCurrent.getTime() - time; // 时间差的毫秒数
        const dayDiff = Math.floor(dateDiff / (24 * 3600 * 1000)); // 计算出相差天数
        if (dayDiff > 365) {
          return (
            `${dateReal.getFullYear()
            }-${
              dateReal.getMonth() + 1
            }-${
              dateReal.getDate()}`
          );
        }
        if (dayDiff >= 2) {
          return `${dateReal.getMonth() + 1}-${dateReal.getDate()}`;
        }
        if (dayDiff >= 1 && dayDiff < 2) {
          return '昨天';
        }

        const leave1 = dateDiff % (24 * 3600 * 1000); // 计算天数后剩余的毫秒数
        const hours = Math.floor(leave1 / (3600 * 1000)); // 计算出小时数
        if (hours >= 1) {
          return `${hours}小时前`;
        }

        // 计算相差分钟数
        const leave2 = leave1 % (3600 * 1000); // 计算小时数后剩余的毫秒数
        const minutes = Math.floor(leave2 / (60 * 1000)); // 计算相差分钟数
        if (minutes > 0) {
          return `${minutes}分钟前`;
        }
        return '刚刚';
      },

      getDateLabel(time) {
        const dateReal = new Date(time); // 时间时间
        const month = (dateReal.getMonth() + 1);
        const day = dateReal.getDate();
        return `${month >= 10 ? month : `0${month}`}-${day >= 10 ? day : `0${day}`}`;
      },

      getSuperProp(key) {
        if (superProps) {
          return superProps[key];
        }
        if (Vue && Vue.Native && Vue.Native.Platform !== 'browser') {
          const paramsStr = getApp().$options.$superProps.params;
          if (paramsStr == null) {
            return null;
          }

          try {
            const params = JSON.parse(paramsStr) || {};
            logger.info('终端传参: ', params);
            superProps = params;
            return superProps[key];
          } catch (err) {
            return paramsStr;
          }
        } else {
          const app = getApp();
          if (app && app.$route) {
            superProps = app.$route.query || {};
          }
          return superProps && superProps[key];
        }
      },
      // 数据上报
      report,
      logger,
      /**
       * 判断是否支持同步方法
       * @return {Boolean}
       * */
      checkSupportFnSync(fn) {
        return Vue.Jsbridge.checkSupportFnSync(fn);
      },
      /**
       * 从UA判断iOS审核开关是否开启
       * @return {Q.Promise<boolean>}
       * @example
       *  this.tools.getSwitchStatus().then(status => { console.log(status); })  //页面使用方式
       * */
      getSwitchStatus() {
        return Vue.Jsbridge.getSwitchStatus();
      },
      /**
       * 从UA判断iOS审核开关是否开启（同步版）
       * @return {Boolean}
       * */
      getSwitchStatusSync() {
        return Vue.Jsbridge.getSwitchStatusSync();
      },
      /**
       * 从UA判断Android审核开关是否开启（同步版）
       * @return {Boolean}
       * */
      getAndroidSwitchStatusSync() {
        return Vue.Jsbridge.getAndroidSwitchStatusSync();
      },
      /**
       * 从UA判断Android审核开关是否开启
       * @return {Boolean}
       * @example
       *  this.tools.getAndroidSwitchStatus().then(status => { console.log(status); })  //页面使用方式
       * */
      getAndroidSwitchStatus() {
        if (Vue.Native) {
          return Vue.Native.callNativeWithPromise('GameCommunityJsModule', 'getHttpHeader')
            .then((data) => {
              const ua = data['User-Agent'];
              const isIOS = (/(iPad|iPhone|iPod)/).test(ua);
              // 不是iOS才进行判断
              if (!isIOS) {
                const matches = ua.match(/\sSUB\/([\d])/);
                if (matches && matches[1] && Number(matches[1]) === 1) {
                  return true;
                }
              }
              return false;
            })
            .catch((err) => {
              console.log('getAndroidSwitchStatus', err);
              return false;
            });
        } // 非Hippy页面，默认不启用开关
        return Promise.resolve(false);
      },
      /**
       * 判断目标版本是否大于当前版本
       * @param targetVersion [String|Object] 安卓IOS统一对比就传字符串 分开对比就传对象 {android: '1.1.0', ios: '1.1.0',}
       * @returns {Promise<number>} -1 当前版本小于目标版本 0 当前版本等于目标版本 1 当前版本大于目标版本
       */
      compareVersion,
      /**
       * 跳转详情页方法
       * @param options {Object} 参数对象
       * @param options.url {String} 要跳转的url
       * @param options.params {Object} 要追加的对象
       * @param options.postInfo {String} 帖子信息结构体 注意内部要带proto字段
       */
      linkToPostDetail({ url, params = {}, postInfo = {} } = {}) {
        logger.debug('webbundle打开详情页 linkToPostDetail', url, params, postInfo);
        if (typeof params !== 'object') params = {};
        url = appendParam(url, params);
        this.compareVersion('1.1.0')
          .then((res) => {
            if (res.code <= 0) {
              this.callNative('callNativeAction', url);
            } else {
              console.log('webBundle active');
              this.callNative('pushPostPageWithSummaryData', getApp().$options.rootViewId, url, JSON.stringify(postInfo));
            }
          });
      },
      /**
       * 跳转沉浸式视频方法
       * @param options {Object} 参数对象
       * @param options.curPostInfo {Object} 进入沉浸式的打开的帖子结构
       * @param options.extraData {Object} 要追加的额外参数
       * extraData 结构
          {
            "need_fetch_feeds": true, // 是否需要拉取后续的feeds
            "click_source": 1, // 当前所处的场景
            "group_id": 0, // 圈子ID，非圈子场景为0
            "uid": 0, // 当前个人主页uid，非个人主页场景可为0
            "all_posts":[], // 当前拉到的视频贴列表
            "need_fetch_rcmd": true, // 是否需要拉取推荐
            "fetch_rcmd_param": { // 拉取推荐的参数
              "group_id": 0,  // 圈子ID（用于推荐某个圈子的视频）
              "click_source": 0 // 推荐池场景（后台根据这个场景推荐不同的内容）
            }
          }
       */
      jumpToImmersiveVedio({ curPostInfo = {}, extraData = {} } = {}) {
        this.compareVersion('1.5.0')
          .then((res) => {
            if (res.code <= 0) {
              // this.callNative('callNativeAction', url);
            } else {
              this.callNative('pushImmersivePostPage', getApp().$options.rootViewId, curPostInfo, extraData);
            }
          });
      },
      /**
       * 获取当前环境 0：正式，2：测试，3：开发
       * */
      getEnv() {
        return Vue.Native.callNativeWithPromise(
          'GameCommunityJsModule',
          'getNetEnvironment',
        ).then(environment => environment);
      },

      /**
      * 全局事件监听器
      */
      registerGlobalNotication(event, fn) {
        if (!globalNotificationObserve.listener[event]) {
          // 如果终端还没注册这个监听器，则注册一次
          getApp().$on(event, (params) => {
            globalNotificationObserve.publish(event, params);
          });
        }
        globalNotificationObserve.on(event, fn);
      },
      unRegisterGlobalNotication(event, fn) {
        globalNotificationObserve.off(event, fn);
      },
    };
  },
};
